import { useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { wsManager } from '../api/websocket'
import { useChatStore, Message } from '../stores/chatStore'
import { useAuthStore } from '../stores/authStore'
import type { TypingMetrics } from './useTypingMetrics'

export function useWebSocket() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const { addMessage, setTherapistStatus, isConnected } = useChatStore()
  const queryClient = useQueryClient()

  // Connect on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      wsManager.connect()

      // Set up message handler
      wsManager.setOnMessage((message: Message) => {
        addMessage(message)
      })

      wsManager.setOnStatus((status) => {
        setTherapistStatus(status)
      })

      wsManager.setOnError((code, message) => {
        console.error(`WebSocket error [${code}]:`, message)
      })

      // Handle handoff events (including onboarding completion)
      // Must mirror TherapistSwitcher cleanup: clear old messages BEFORE
      // query invalidation to prevent rendering old messages with new avatar.
      wsManager.setOnHandoff((from, to) => {
        console.log(`Handoff from ${from} to ${to}`)
        // If handoff is from finch/harold, mark onboarding as complete
        if (from === 'finch' || from === 'harold') {
          localStorage.setItem('onboarding_complete', 'true')
        }
        // Clear old messages immediately — prevents mixed therapist content
        useChatStore.getState().clearMessages()
        // Reset therapist status (prevents stuck "typing" from old therapist)
        useChatStore.getState().setTherapistStatus('idle')
        // Clear react-query cache BEFORE invalidating — otherwise the stale
        // cached historyData re-populates zustand via ChatPage's useEffect
        queryClient.setQueryData(['chatHistory'], null)
        // Invalidate queries to load new therapist's chat history
        queryClient.invalidateQueries({ queryKey: ['currentTherapist'] })
        queryClient.invalidateQueries({ queryKey: ['chatHistory'] })
      })

      return () => {
        wsManager.disconnect()
      }
    }
  }, [isAuthenticated, addMessage, setTherapistStatus, queryClient])

  const sendMessage = useCallback((content: string, typingMetrics?: TypingMetrics) => {
    wsManager.sendMessage(content, typingMetrics)
  }, [])

  const startVoiceRecording = useCallback(() => {
    wsManager.startVoiceRecording()
  }, [])

  const sendVoiceChunk = useCallback((base64Data: string) => {
    wsManager.sendVoiceChunk(base64Data)
  }, [])

  const endVoiceRecording = useCallback((durationSeconds?: number) => {
    wsManager.endVoiceRecording(durationSeconds)
  }, [])

  return {
    isConnected,
    sendMessage,
    startVoiceRecording,
    sendVoiceChunk,
    endVoiceRecording,
  }
}
